import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FileUploader } from 'react-drag-drop-files';
import { Body5, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import ErrorText from '@ui/ErrorText';
import { colorThemeActions } from '@constants/actionTypes';
import { parseThemeImport, saveColorTheme, _getThemesFromStorage } from '@utils/themes';
import { showSuccessBanner, showErrorBanner } from '@utils/application';

const WarningText = styled(Body5)`
  color: ${({ theme }) => theme.themeColors.warning};
`;

const DropZoneWrapper = styled.div`
  position: relative;
  border: 2px dashed ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 4px;
  min-height: 80px;
  width: 100%;
  max-width: 360px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;

  label {
    border: none;
    width: 100%;
  }
`;

const ConfirmWrapper = styled.div`
  max-width: 360px;
`;

const NameInput = styled.input`
  font-size: 14px;
  padding: 8px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  background: ${({ theme }) => theme.themeColors.appBackground};
  color: ${({ theme }) => theme.themeColors.text};
`;

const STATES = { IDLE: 'IDLE', CONFIRM: 'CONFIRM', ERROR: 'ERROR' };

export const ThemeImporter = ({ onImported }) => {
  const dispatch = useDispatch();

  const [view, setView] = React.useState(STATES.IDLE);
  const [errorMsg, setErrorMsg] = React.useState('');
  const [parsedColors, setParsedColors] = React.useState(null);
  const [importedName, setImportedName] = React.useState('');
  const [uploadKey, setUploadKey] = React.useState(0);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = parseThemeImport(e.target.result, file.name.replace(/\.json$/i, ''));
      if (!result.ok) {
        setErrorMsg(result.error);
        setView(STATES.ERROR);
        showErrorBanner(result.error, {}, dispatch);
        return;
      }
      setParsedColors(result.colors);
      setImportedName(result.name);
      setView(STATES.CONFIRM);
    };
    reader.onerror = () => {
      const msg = 'Failed to read file.';
      setErrorMsg(msg);
      setView(STATES.ERROR);
      showErrorBanner(msg, {}, dispatch);
    };
    reader.readAsText(file);
  };

  const handleSave = () => {
    const name = importedName.trim() || 'Imported Theme';
    saveColorTheme(name, parsedColors);
    onImported();
    showSuccessBanner(`Saved theme "${name}"`, {}, dispatch);
    resetState();
  };

  const handleLoad = () => {
    dispatch({
      type: colorThemeActions.setCustomColorThemeColorDefinition,
      payload: { ...parsedColors },
    });
    showSuccessBanner('Theme loaded into draft.', {}, dispatch);
    resetState();
  };

  const resetState = () => {
    setView(STATES.IDLE);
    setParsedColors(null);
    setImportedName('');
    setErrorMsg('');
    setUploadKey((k) => k + 1);
  };

  if (view === STATES.CONFIRM) {
    const existingThemes = _getThemesFromStorage();
    const nameCollision = importedName.trim() && importedName.trim() in existingThemes;
    return (
      <ConfirmWrapper>
        <Container bottom={8}>
          <Body5 bold>Import Theme</Body5>
        </Container>
        <Container bottom={8}>
          <NameInput
            value={importedName}
            onChange={(e) => setImportedName(e.target.value)}
            placeholder="Theme name"
          />
        </Container>
        {nameCollision && (
          <Container bottom={8}>
            <WarningText>⚠ A theme with this name already exists. Saving will overwrite it.</WarningText>
          </Container>
        )}
        <Container row top={8}>
          <Container>
            <FlatButton onClick={handleSave}>Save to My Themes</FlatButton>
          </Container>
          <Container left={8}>
            <SkeletonButton onClick={handleLoad}>Load Without Saving</SkeletonButton>
          </Container>
          <Container left={8}>
            <TransparentFlatButton onClick={resetState}>Cancel</TransparentFlatButton>
          </Container>
        </Container>
      </ConfirmWrapper>
    );
  }

  return (
    <Container bottom={16}>
      <Container bottom={8}>
        <Body5 bold>Import Theme</Body5>
      </Container>
      {view === STATES.ERROR && (
        <Container bottom={8}>
          <ErrorText>{errorMsg}</ErrorText>
        </Container>
      )}
      <DropZoneWrapper>
        <FileUploader
          key={uploadKey}
          handleChange={handleFile}
          name="theme-file"
          types={['JSON']}
          multiple={false}
          hoverTitle=""
          dropMessageStyle={{ display: 'none' }}
          children={
            <Container all={16}>
              <Body5 isLowContrast>Drag and drop a .tornado-theme.json file, or click to browse.</Body5>
            </Container>
          }
        />
      </DropZoneWrapper>
    </Container>
  );
};
